import React, { useEffect, useMemo, useState } from "react";
import "../styles/TeamLeitung.css";
import { getTeachers, getTeamPage } from "../lib/api";
import IconByName from "../components/IconByName";
import { Page, PageHeader, Section, FilterChips } from "../components/ui";

const CATEGORY_ORDER = [
  { key: "leitung-sekretariat", label: "Leitung & Sekretariat" },
  { key: "paedagoginnen-paedagogen", label: "Pädagoginnen & Pädagogen" },
  { key: "supportteam", label: "Supportteam" },
  { key: "facility-management", label: "Facility Management" },
];

// Catch-all so entries without a (yet) assigned category still render
const FALLBACK_CATEGORY = { key: "weiteres-team", label: "Weiteres Team" };

function groupTeachers(teachers) {
  const buckets = new Map();
  [...CATEGORY_ORDER, FALLBACK_CATEGORY].forEach((c) => buckets.set(c.key, []));

  teachers.forEach((t) => {
    const key = buckets.has(t.category) ? t.category : FALLBACK_CATEGORY.key;
    buckets.get(key).push(t);
  });

  return [...CATEGORY_ORDER, FALLBACK_CATEGORY]
    .map((c) => ({ ...c, teachers: buckets.get(c.key) }))
    .filter((group) => group.teachers.length > 0);
}

function scrollToCategory(key) {
  const el = document.getElementById(`team-group-${key}`);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function TeamLeitung() {
  const [page, setPage] = useState(null);
  const [teachers, setTeachers] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    Promise.all([getTeamPage(), getTeachers()])
      .then(([pageRes, teachersRes]) => {
        if (!cancelled) {
          setPage(pageRes);
          setTeachers(teachersRes);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const groups = useMemo(() => (teachers ? groupTeachers(teachers) : []), [teachers]);

  return (
    <Page className="team-page" loading={!teachers} error={error}>
      <PageHeader
        parent={{ label: "Über Uns", to: "/" }}
        current="Team & Leitung"
        title={page?.heroTitle || "Team & Leitung"}
        subtitle={page?.heroSubtitle}
      />

      <Section>
        {/* Wrapper pulls the pills up under the header (see .team-filters
            in TeamLeitung.css), matching Downloads/FAQ. */}
        {groups.length > 1 ? (
          <div className="team-filters">
            <FilterChips
              options={groups.map((g) => ({ value: g.key, label: g.label }))}
              value={null}
              showAll={false}
              label="Teambereiche"
              onChange={scrollToCategory}
            />
          </div>
        ) : null}

        {groups.map((group) => (
          <div key={group.key} className="team-group">
            <h2 id={`team-group-${group.key}`} className="team-group__title">
              {group.label}
            </h2>

            {group.key === "leitung-sekretariat" &&
            (page?.terminPhone || page?.terminEmail) ? (
              <div className="termin-box">
                <p className="termin-box__title">
                  {page?.terminTitle || "Terminvereinbarung"}
                </p>
                {page?.terminText ? (
                  <p className="termin-box__text">{page.terminText}</p>
                ) : null}
                <p className="termin-box__contact">
                  {page?.terminPhone ? <span>{page.terminPhone}</span> : null}
                  {page?.terminPhone && page?.terminEmail ? (
                    <span className="termin-box__sep">·</span>
                  ) : null}
                  {page?.terminEmail ? <span>{page.terminEmail}</span> : null}
                </p>
              </div>
            ) : null}

            <div className="teacher-grid">
              {group.teachers.map((teacher) => (
                <TeacherCard key={teacher.id} teacher={teacher} />
              ))}
            </div>
          </div>
        ))}
      </Section>
    </Page>
  );
}

function TeacherCard({ teacher }) {
  return (
    <article className="teacher-card">
      {teacher.email ? (
        <a
          className="teacher-card__mail"
          href={`mailto:${teacher.email}`}
          aria-label={`E-Mail an ${teacher.name}`}
        >
          <IconByName name="mail" size={18} />
        </a>
      ) : null}

      <figure className="teacher-photo">
        {teacher.photo ? (
          <img
            className="teacher-photo__base"
            src={teacher.photo.url}
            alt={teacher.name}
            loading="lazy"
          />
        ) : null}

        {teacher.drawing ? (
          <img
            className="teacher-photo__drawing"
            src={teacher.drawing.url}
            alt={`Kinderzeichnung von ${teacher.name}`}
            loading="lazy"
          />
        ) : null}

        <figcaption className="teacher-photo__hint">Kinderzeichnung</figcaption>
      </figure>

      <div className="teacher-card__body">
        {teacher.role ? <span className="teacher-role">{teacher.role}</span> : null}

        <h3>{teacher.name}</h3>

        {teacher.phoneNumber ? <p className="teacher-phone">{teacher.phoneNumber}</p> : null}

        <ul className="teacher-subjects" aria-label="Schwerpunkte">
          {(teacher.subjects || []).map((subject) => (
            <li key={subject}>{subject}</li>
          ))}
        </ul>

        {teacher.text ? <p className="teacher-text">{teacher.text}</p> : null}
      </div>
    </article>
  );
}